import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { toast } from 'react-toastify';

import { Player } from 'modules/players/domain/interfaces/player';
import { addTournamentUserToTeam } from 'modules/players/store/actions';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TeamPickerModal } from 'modules/teams/ui/components/team-picker-modal';
import { useAppDispatch } from 'store/hooks';

/**
 * Модалка «Добавление в команду» на карточке игрока.
 *
 * Выбор команды — общий `TeamPickerModal`, тот же, что в заявках соревнования: список
 * команд организации виден сразу при открытии, а команды, где игрок уже состоит, видны
 * отключёнными с пометкой. Проверка при отправке всё равно остаётся: отключение опции —
 * подсказка в интерфейсе, а не гарантия (данные игрока могли устареть).
 */
function useModalAddToTeam() {
  const { t } = useLingui();
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [player, setPlayer] = useState<Player>();

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const playerTeamIds = useMemo(
    () => (player?.positionsInTeams ?? []).map((position) => position?.team?.id).filter((id): id is number => Boolean(id)),
    [player],
  );

  const handleSubmit = useCallback(
    async (teams: Team[]) => {
      const teamId = teams[0]?.id;
      if (!teamId) return;

      if (!player?.id) {
        toast.error(t`Не удалось определить игрока, перезагрузите страницу и попробуйте снова`);
        return;
      }

      if (playerTeamIds.includes(teamId)) {
        toast.warn(t`Игрок уже состоит в команде`);
        return;
      }

      try {
        await dispatch(addTournamentUserToTeam({ teamId, userId: player.id })).unwrap();
      } catch (error) {
        console.error(error);
        if (error === 'User is already in this team') {
          toast.warn(t`Игрок уже состоит в команде`);
        } else {
          toast.error(t`Ошибка при добавлении игрока в команду, попробуйте ещё раз или обратитесь в поддержку`);
        }
        return;
      }

      close();
      toast.success(t`Игрок добавлен в команду`);
    },
    [close, dispatch, player, playerTeamIds, t],
  );

  const Component = useCallback(() => {
    if (!isOpen) return null;

    return (
      <TeamPickerModal
        title={t`Добавление в команду`}
        subtitle={t`Команды берутся из вашей организации.`}
        submitLabel={t`Добавить`}
        disabledTeamIds={playerTeamIds}
        disabledHint={t`уже в команде`}
        onSubmit={handleSubmit}
        onClose={close}
      />
    );
  }, [close, handleSubmit, isOpen, playerTeamIds, t]);

  const openModal = useCallback((player: Player) => {
    setPlayer(player);
    setIsOpen(true);
  }, []);

  return { open: openModal, close, Modal: Component };
}

export default useModalAddToTeam;
